import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ArrowLeft, Copy, ExternalLink, Trophy, Video, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { AdminGate } from "@/components/admin/AdminGate";
import { getBibliotecaAnuncios } from "@/lib/ads/biblioteca.functions";
import type { AnuncioLib } from "@/lib/ads/biblioteca.server";
import { PRODUTOS, TIPOS_CAMPANHA, produtosDoAnuncio, tipoCampanha, type Produto, type TipoCampanha } from "@/lib/ads/biblioteca-classificacao";
import MODELOS from "@/lib/ads/modelos-black26.json";

export const Route = createFileRoute("/admin/biblioteca-anuncios")({
  head: () => ({
    meta: [
      { title: "Admin · Biblioteca de Anúncios" },
      { name: "description", content: "Criativos do Meta Ads da FEO com métricas, ranking e referências." },
      { property: "og:title", content: "Admin · Biblioteca de Anúncios" },
      { property: "og:description", content: "Criativos do Meta Ads da FEO com métricas, ranking e referências." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => <AdminGate route="/admin/biblioteca-anuncios"><BibliotecaPage /></AdminGate>,
});

const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const num = (v: number) => v.toLocaleString("pt-BR");
const iso = (d: Date) => d.toISOString().slice(0, 10);
const diasAtras = (n: number) => iso(new Date(Date.now() - n * 86400000));

type Ordem = "cpl" | "leads" | "gasto" | "ctr" | "cliques" | "compras";
const PERIODOS = [{ k: 30, l: "30 dias" }, { k: 90, l: "90 dias" }, { k: 180, l: "6 meses" }, { k: 365, l: "12 meses" }];
type Item = AnuncioLib & { tipo: TipoCampanha; produtos: Produto[] };

function BibliotecaPage() {
  const fetchFn = useServerFn(getBibliotecaAnuncios);
  const [aba, setAba] = useState<"anuncios" | "modelos">("anuncios");
  const [dias, setDias] = useState(90);
  const [conta, setConta] = useState("todas");
  const [formato, setFormato] = useState("todos");
  const [tipo, setTipo] = useState("todos");
  const [produto, setProduto] = useState("todos");
  const [status, setStatus] = useState("todos");
  const [soRef, setSoRef] = useState(false);
  const [minLeads, setMinLeads] = useState("");
  const [maxCpl, setMaxCpl] = useState("");
  const [minGasto, setMinGasto] = useState("");
  const [minCtr, setMinCtr] = useState("");
  const [ordem, setOrdem] = useState<Ordem>("cpl");
  const [busca, setBusca] = useState("");
  const de = diasAtras(dias), ate = diasAtras(0);
  const q = useQuery({ queryKey: ["biblioteca-anuncios", de, ate], queryFn: () => fetchFn({ data: { de, ate } }), staleTime: 15 * 60_000 });

  const todos: Item[] = useMemo(() => (q.data?.anuncios ?? []).map((a) => ({ ...a, tipo: tipoCampanha(a), produtos: produtosDoAnuncio(a) })), [q.data]);
  const contas = useMemo(() => Array.from(new Set(todos.map((a) => a.conta))).sort(), [todos]);
  // Referência: CPL no melhor quartil entre anúncios com ao menos 10 leads.
  const corteRef = useMemo(() => {
    const cpls = todos.filter((a) => a.leads >= 10 && a.cpl != null).map((a) => a.cpl!).sort((a, b) => a - b);
    return cpls.length ? cpls[Math.floor(cpls.length * 0.25)] : null;
  }, [todos]);
  const isRef = (a: Item) => corteRef != null && a.leads >= 10 && a.cpl != null && a.cpl <= corteRef;

  const lista = useMemo(() => {
    const b = busca.trim().toLowerCase();
    const nL = Number(minLeads) || 0, xC = Number(maxCpl.replace(",", ".")) || 0, nG = Number(minGasto) || 0, nT = Number(minCtr.replace(",", ".")) || 0;
    const l = todos.filter((a) =>
      (conta === "todas" || a.conta === conta) && (formato === "todos" || a.formato === formato) &&
      (tipo === "todos" || a.tipo === tipo) && (produto === "todos" || a.produtos.includes(produto as Produto)) &&
      (status === "todos" || (status === "ativo" ? a.status === "ACTIVE" : a.status !== "ACTIVE")) &&
      (!soRef || isRef(a)) && a.leads >= nL && (!xC || (a.cpl != null && a.cpl <= xC)) && a.gasto >= nG && a.ctr >= nT &&
      (!b || `${a.nome} ${a.campanha} ${a.conjunto} ${a.titulo ?? ""} ${a.texto ?? ""}`.toLowerCase().includes(b)));
    const s = [...l];
    if (ordem === "cpl") s.sort((x, y) => (x.leads >= 5 ? 0 : 1) - (y.leads >= 5 ? 0 : 1) || (x.cpl ?? 1e9) - (y.cpl ?? 1e9));
    if (ordem === "leads") s.sort((x, y) => y.leads - x.leads);
    if (ordem === "gasto") s.sort((x, y) => y.gasto - x.gasto);
    if (ordem === "cliques") s.sort((x, y) => y.cliques - x.cliques);
    if (ordem === "compras") s.sort((x, y) => y.compras - x.compras);
    if (ordem === "ctr") s.sort((x, y) => (y.impressoes >= 1000 ? y.ctr : 0) - (x.impressoes >= 1000 ? x.ctr : 0));
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todos, conta, formato, tipo, produto, status, soRef, minLeads, maxCpl, minGasto, minCtr, ordem, busca, corteRef]);

  const tot = lista.reduce((m, a) => ({ gasto: m.gasto + a.gasto, leads: m.leads + a.leads, imp: m.imp + a.impressoes, cli: m.cli + a.cliques }), { gasto: 0, leads: 0, imp: 0, cli: 0 });
  const porTipo = useMemo(() => TIPOS_CAMPANHA.map((t) => {
    const l = lista.filter((a) => a.tipo === t); const g = l.reduce((m, a) => m + a.gasto, 0), le = l.reduce((m, a) => m + a.leads, 0);
    return { t, n: l.length, g, le };
  }).filter((x) => x.n), [lista]);
  const sel = "rounded-lg border border-black-campaign-purple/40 bg-black-campaign-surface px-3 py-2 text-sm text-foreground";
  const limpar = () => { setConta("todas"); setFormato("todos"); setTipo("todos"); setProduto("todos"); setStatus("todos"); setSoRef(false); setMinLeads(""); setMaxCpl(""); setMinGasto(""); setMinCtr(""); setBusca(""); };

  return (
    <div className="min-h-screen bg-background px-4 py-8 text-foreground sm:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Admin</Link>
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-[0.15em] text-black-campaign-lilac">Biblioteca de Anúncios</h1>
          <p className="mt-1 text-sm text-muted-foreground">Criativos que rodaram no Meta Ads (Facebook/Instagram) em todas as contas da FEO, com resultados reais.</p>
        </div>
        <div className="flex gap-2">
          {([["anuncios", "Anúncios"], ["modelos", "Modelos Black 2026"]] as const).map(([k, l]) => (
            <button key={k} onClick={() => setAba(k)} className={`rounded-lg px-4 py-2 text-sm font-semibold ${aba === k ? "bg-black-campaign-purple text-foreground" : "border border-black-campaign-purple/40 text-muted-foreground"}`}>{l}</button>
          ))}
        </div>

        {aba === "modelos" ? <Modelos /> : <>
        <div className="space-y-2 rounded-xl border border-black-campaign-purple/40 bg-black-campaign-surface/50 p-3">
          <div className="flex flex-wrap gap-2">
            <select className={sel} value={dias} onChange={(e) => setDias(Number(e.target.value))}>{PERIODOS.map((p) => <option key={p.k} value={p.k}>{p.l}</option>)}</select>
            <select className={sel} value={tipo} onChange={(e) => setTipo(e.target.value)}><option value="todos">Todos os tipos de campanha</option>{TIPOS_CAMPANHA.map((t) => <option key={t}>{t}</option>)}</select>
            <select className={sel} value={produto} onChange={(e) => setProduto(e.target.value)}><option value="todos">Todos os produtos</option>{PRODUTOS.map((t) => <option key={t}>{t}</option>)}</select>
            <select className={sel} value={formato} onChange={(e) => setFormato(e.target.value)}><option value="todos">Todos os formatos</option><option value="video">Vídeo</option><option value="imagem">Imagem</option><option value="carrossel">Carrossel</option></select>
            <select className={sel} value={conta} onChange={(e) => setConta(e.target.value)}><option value="todas">Todas as contas</option>{contas.map((c) => <option key={c}>{c}</option>)}</select>
            <select className={sel} value={status} onChange={(e) => setStatus(e.target.value)}><option value="todos">Ativos e pausados</option><option value="ativo">Só ativos</option><option value="pausado">Só pausados</option></select>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input className={`${sel} w-32`} inputMode="numeric" placeholder="Leads mín." value={minLeads} onChange={(e) => setMinLeads(e.target.value)} />
            <input className={`${sel} w-32`} inputMode="decimal" placeholder="CPL máx. R$" value={maxCpl} onChange={(e) => setMaxCpl(e.target.value)} />
            <input className={`${sel} w-32`} inputMode="numeric" placeholder="Gasto mín. R$" value={minGasto} onChange={(e) => setMinGasto(e.target.value)} />
            <input className={`${sel} w-32`} inputMode="decimal" placeholder="CTR mín. %" value={minCtr} onChange={(e) => setMinCtr(e.target.value)} />
            <select className={sel} value={ordem} onChange={(e) => setOrdem(e.target.value as Ordem)}><option value="cpl">Melhor CPL</option><option value="leads">Mais leads</option><option value="gasto">Maior gasto</option><option value="ctr">Melhor CTR</option><option value="cliques">Mais cliques</option><option value="compras">Mais compras</option></select>
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={soRef} onChange={(e) => setSoRef(e.target.checked)} /> Só referências</label>
            <input className={`${sel} min-w-[200px] flex-1`} placeholder="Buscar por nome, campanha ou texto…" value={busca} onChange={(e) => setBusca(e.target.value)} />
            <button onClick={limpar} className="text-xs text-muted-foreground underline">Limpar filtros</button>
          </div>
        </div>

        {q.isLoading && <p className="text-sm text-muted-foreground">Carregando anúncios do Meta Ads… (pode levar alguns segundos)</p>}
        {q.error && <p className="text-sm text-destructive">Erro: {(q.error as Error).message}</p>}
        {q.data?.erro && <p className="text-xs text-muted-foreground">Aviso: {q.data.erro}</p>}

        {q.data && (<>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[["Anúncios", num(lista.length)], ["Investido", brl(tot.gasto)], ["Leads", num(tot.leads)], ["CPL médio", tot.leads ? brl(tot.gasto / tot.leads) : "—"], ["CTR médio", tot.imp ? `${((tot.cli / tot.imp) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%` : "—"], ["Referências", num(lista.filter(isRef).length)]].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-black-campaign-purple/40 bg-black-campaign-surface p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{k}</p>
                <p className="mt-1 text-xl font-bold">{v}</p>
              </div>
            ))}
          </div>
          {porTipo.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {porTipo.map((x) => (
                <button key={x.t} onClick={() => setTipo(tipo === x.t ? "todos" : x.t)} className={`rounded-lg border px-3 py-2 text-left text-xs ${tipo === x.t ? "border-black-campaign-cyan" : "border-black-campaign-purple/40"} bg-black-campaign-surface`}>
                  <p className="font-semibold">{x.t}</p>
                  <p className="text-muted-foreground">{x.n} anúncios · {brl(x.g)} · CPL {x.le ? brl(x.g / x.le) : "—"}</p>
                </button>
              ))}
            </div>
          )}
        </>)}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {lista.map((a, i) => <Card key={a.id} a={a} rank={i + 1} ref_={isRef(a)} />)}
        </div>
        {q.data && !lista.length && <p className="text-sm text-muted-foreground">Nenhum anúncio com gasto nesse filtro.</p>}
        </>}
      </div>
    </div>
  );
}

function Modelos() {
  const copiar = (t: string) => navigator.clipboard.writeText(t).then(() => toast.success("Copiado"));
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">10 modelos criados a partir dos anúncios campeões da lista Black 2026 (CPL e escala). As artes em alta estão no pacote .zip entregue ao time.</p>
      <div className="grid gap-4 md:grid-cols-2">
        {MODELOS.map((m) => (
          <article key={m.n} className="flex flex-col gap-2 rounded-xl border border-black-campaign-purple/40 bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">#{String(m.n).padStart(2, "0")} · {m.tipo} · inspirado em {m.ref}</p>
            <p className="font-semibold text-black-campaign-lilac">{m.titulo}</p>
            <p className="whitespace-pre-line text-xs text-muted-foreground">{m.texto}</p>
            {m.cards.length > 0 && <ol className="list-decimal pl-5 text-xs">{m.cards.map((c) => <li key={c}>{c}</li>)}</ol>}
            {m.roteiro.length > 0 && <div className="space-y-1 rounded-lg bg-black-campaign-surface p-2 text-xs">{m.roteiro.map((r) => <p key={r.tempo}><b className="text-black-campaign-cyan">{r.tempo} · {r.bloco}:</b> {r.fala}</p>)}</div>}
            <div className="mt-auto flex gap-2 pt-2">
              <button onClick={() => copiar(`${m.titulo}\n\n${m.texto}\n\nCTA: ${m.cta}`)} className="inline-flex items-center gap-1 rounded-lg border border-black-campaign-purple/40 px-3 py-1.5 text-xs hover:bg-black-campaign-surface"><Copy className="h-3 w-3" /> Copiar copy</button>
              {m.roteiro.length > 0 && <button onClick={() => copiar(m.roteiro.map((r) => `${r.tempo} ${r.bloco}: ${r.fala}`).join("\n"))} className="inline-flex items-center gap-1 rounded-lg border border-black-campaign-purple/40 px-3 py-1.5 text-xs hover:bg-black-campaign-surface"><Copy className="h-3 w-3" /> Copiar roteiro</button>}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function Card({ a, rank, ref_ }: { a: Item; rank: number; ref_: boolean }) {
  const copiar = () => {
    const t = [a.titulo, a.texto].filter(Boolean).join("\n\n");
    if (!t) return toast.error("Anúncio sem texto");
    navigator.clipboard.writeText(t).then(() => toast.success("Copy copiada"));
  };
  return (
    <article className={`flex flex-col overflow-hidden rounded-xl border bg-card ${ref_ ? "border-black-campaign-cyan" : "border-black-campaign-purple/40"}`}>
      <div className="relative aspect-square bg-black-campaign-surface">
        {a.imagem ? <img src={a.imagem} alt={a.nome} loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-muted-foreground">Sem prévia</div>}
        <span className="absolute left-2 top-2 rounded bg-background/85 px-2 py-0.5 text-xs font-bold">#{rank}</span>
        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded bg-background/85 px-2 py-0.5 text-xs">
          {a.formato === "video" ? <Video className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}{a.formato}
        </span>
        {ref_ && <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded bg-black-campaign-cyan px-2 py-0.5 text-xs font-bold text-background"><Trophy className="h-3 w-3" /> Referência</span>}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{a.conta} · {a.status ?? "—"}</p>
        <p className="line-clamp-2 text-sm font-semibold">{a.nome}</p>
        <p className="line-clamp-1 text-xs text-muted-foreground">{a.campanha}</p>
        <div className="flex flex-wrap gap-1 text-[10px] font-semibold"><span className="rounded bg-black-campaign-purple/30 px-1.5 py-0.5">{a.tipo}</span>{a.produtos.map((p) => <span key={p} className="rounded bg-black-campaign-cyan/20 px-1.5 py-0.5">{p}</span>)}{a.compras > 0 && <span className="rounded bg-black-campaign-lilac/20 px-1.5 py-0.5">{a.compras} compras</span>}</div>
        <div className="grid grid-cols-3 gap-2 rounded-lg bg-black-campaign-surface p-2 text-center text-xs">
          <div><p className="text-muted-foreground">Gasto</p><p className="font-bold">{brl(a.gasto)}</p></div>
          <div><p className="text-muted-foreground">Leads</p><p className="font-bold">{num(a.leads)}</p></div>
          <div><p className="text-muted-foreground">CPL</p><p className="font-bold">{a.cpl != null ? brl(a.cpl) : "—"}</p></div>
          <div><p className="text-muted-foreground">Impr.</p><p className="font-bold">{num(a.impressoes)}</p></div>
          <div><p className="text-muted-foreground">Cliques</p><p className="font-bold">{num(a.cliques)}</p></div>
          <div><p className="text-muted-foreground">CTR</p><p className="font-bold">{a.ctr.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%</p></div>
        </div>
        {a.titulo && <p className="text-sm font-semibold">{a.titulo}</p>}
        {a.texto && <p className="line-clamp-5 whitespace-pre-line text-xs text-muted-foreground">{a.texto}</p>}
        <div className="mt-auto flex flex-wrap gap-2 pt-2">
          <button onClick={copiar} className="inline-flex items-center gap-1 rounded-lg border border-black-campaign-purple/40 px-3 py-1.5 text-xs hover:bg-black-campaign-surface"><Copy className="h-3 w-3" /> Copiar copy</button>
          {a.preview && <a href={a.preview} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-black-campaign-purple/40 px-3 py-1.5 text-xs hover:bg-black-campaign-surface"><ExternalLink className="h-3 w-3" /> Ver anúncio</a>}
        </div>
      </div>
    </article>
  );
}
